import { action, observable, runInAction } from 'mobx';

import {
  type BaseP2pMessage,
  BaseP2pMessageType,
  isValidSyncOrAcknowledgeP2pMessage,
  type PlayP2pMessage,
} from '@genially/p2p-lib';

import { createLogger, LoggerLevel } from '../../../util/logger';
import type { P2pClient } from '../../P2pClient';
import { P2pClientEventType } from '../../P2pClient';
import { calculateStringSizeInKb } from '../implementations/utils/calculateStringSizeInKb';

export class DebugInfo {
  protected readonly _logger = createLogger({
    prefix: 'DebugInfo',
    minLoggerLevel: LoggerLevel.Error,
  });

  @observable
  protected _data: {
    connectedToPlayerInitialTimes: Record<string, number>;
    connectedToPlayerLastMessageReceivedTime: Record<string, number>;

    sentMessageTypePerPeerId: Record<string, Record<string, number>>;
    sentKBytesPerPeerId: Record<string, number>;

    receivedKBytesPerPeerId: Record<string, number>;
    receivedMessageTypePerPeerId: Record<string, Record<string, number>>;

    receivedMessageTypeFromServer: Record<string, number>;
  } = {
    connectedToPlayerInitialTimes: {},
    connectedToPlayerLastMessageReceivedTime: {},

    sentMessageTypePerPeerId: {},
    sentKBytesPerPeerId: {},

    receivedMessageTypePerPeerId: {},
    receivedKBytesPerPeerId: {},

    receivedMessageTypeFromServer: {},
  };

  @observable
  private reconnections = -1;

  constructor(private readonly client: P2pClient<any, any, any, any>) {
    const windowAsAny = window as any;

    if (windowAsAny) {
      const showDebugInfo = () => {
        console.log(`[DebugInfo] Debug info`, JSON.stringify(this.data, null, '\t'));
      };

      windowAsAny.showDebugInfo = showDebugInfo;

      let interval: number | undefined;
      windowAsAny.showDebugInfoInIntervalsOf = (intervalInMs: number) => {
        if (intervalInMs <= 0) {
          return;
        }

        if (interval) {
          clearInterval(interval);
        }

        interval = window.setInterval(showDebugInfo, intervalInMs);
      };

      windowAsAny.stopDebugInfoInterval = () => {
        if (interval) {
          clearInterval(interval);
        }
      };

      windowAsAny.sendDebugInfoToServer = () => {
        this._logger.info('Sending debug info to server', this.data);

        console.info('Thanks for using Genially! 🚀');
      };
    }

    client.on(P2pClientEventType.ConnectedToServer, () => {
      runInAction(() => {
        this.reconnections += 1;
      });
    });
  }

  @action
  onPeerConnectedOpen(peerId: string) {
    this._data.connectedToPlayerInitialTimes[peerId] = Date.now();
  }

  @action
  onMessageReceivedFromServer(data: any) {
    if ('type' in data) {
      const receivedMessageType = this._data.receivedMessageTypeFromServer;
      const receivedMessageTypeAmount = receivedMessageType?.[data.type] ?? 0;

      this._data.receivedMessageTypeFromServer = {
        ...receivedMessageType,
        [data.type]: receivedMessageTypeAmount + 1,
      };
    }
  }

  @action
  onPeerMessageReceived(peerId: string, data: any) {
    const receivedKBytes = this._data.receivedKBytesPerPeerId[peerId] ?? 0;

    this._data.receivedKBytesPerPeerId[peerId] =
      receivedKBytes + calculateStringSizeInKb(JSON.stringify(data));

    if ('type' in data) {
      const receivedMessageType = this._data.receivedMessageTypePerPeerId[peerId];
      const receivedMessageTypeAmount = receivedMessageType?.[data.type] ?? 0;

      if (isValidSyncOrAcknowledgeP2pMessage<PlayP2pMessage | BaseP2pMessage>(data)) {
        const isSync = data.type === BaseP2pMessageType.Sync;

        if (isSync) {
          const receivedMessageTypeOfSync =
            receivedMessageType?.[BaseP2pMessageType.Sync] ?? 0;

          const receivedMessageTypeOfSyncMessage =
            receivedMessageType?.[data.payload.message.type] ?? 0;

          this._data.receivedMessageTypePerPeerId[peerId] = {
            ...receivedMessageType,
            [data.payload.message.type]: receivedMessageTypeOfSyncMessage + 1,
            [BaseP2pMessageType.Sync]: receivedMessageTypeOfSync + 1,
          };
        }
      } else {
        this._data.receivedMessageTypePerPeerId[peerId] = {
          ...receivedMessageType,
          [data.type]: receivedMessageTypeAmount + 1,
        };
      }
    }

    const lastMessageReceivedTime =
      this._data.connectedToPlayerLastMessageReceivedTime[peerId];

    if (lastMessageReceivedTime) {
      this._data.connectedToPlayerLastMessageReceivedTime[peerId] =
        Date.now() - lastMessageReceivedTime;
    } else {
      this._data.connectedToPlayerLastMessageReceivedTime[peerId] = Date.now();
    }
  }

  @action
  onPeerDisconnected(peerId: string) {
    delete this._data.connectedToPlayerInitialTimes[peerId];
  }

  @action
  onPeerMessageSent(peerId: string, data: any) {
    const sentKBytes = this._data.sentKBytesPerPeerId[peerId] ?? 0;

    this._data.sentKBytesPerPeerId[peerId] =
      sentKBytes + calculateStringSizeInKb(JSON.stringify(data));

    if ('type' in data) {
      const sentMessageType = this._data.sentMessageTypePerPeerId[peerId];
      const sentMessageTypeAmount = sentMessageType?.[data.type] ?? 0;

      this._data.sentMessageTypePerPeerId[peerId] = {
        ...sentMessageType,
        [data.type]: sentMessageTypeAmount + 1,
      };
    }
  }

  get data() {
    const { peerId } = this.client;

    const { receivedKBytesPerPeerId, connectedToPlayerLastMessageReceivedTime } =
      this._data;

    return {
      peerId,
      reconnectionsToServer: this.reconnections,
      connectedToPlayerInitialTimes: this.connectedToPlayerInitialTimes,
      connectedToPlayerLastMessageReceivedTime,
      sentKBytes: this.sentKBytes,
      sentMessageType: this.sentMessageType,

      receivedKBytes: this.receivedKBytes,
      receivedMessageType: this.receivedMessageType,

      receivedMessageTypeFromServer: this._data.receivedMessageTypeFromServer,

      receivedKBytesPerPeerId,
    };
  }

  get connectedToPlayerInitialTimes() {
    return Object.entries(this._data.connectedToPlayerInitialTimes).map(
      ([peerId, time]) => ({ peerId, time: Date.now() - time }),
    );
  }

  get receivedMessageType() {
    return Object.entries(this._data.receivedMessageTypePerPeerId).reduce(
      (accumulated, [_, messages]) =>
        Object.entries(messages).reduce(
          (acc, [messageType, count]) => ({
            ...acc,
            [messageType]: (acc[messageType] || 0) + count,
          }),
          accumulated,
        ),
      {} as any,
    );
  }

  get receivedKBytes() {
    return Object.values(this._data.receivedKBytesPerPeerId).reduce(
      (acc, bytes) => acc + bytes,
      0,
    );
  }

  get sentMessageType() {
    return Object.entries(this._data.sentMessageTypePerPeerId).reduce(
      (accumulated, [_, messages]) =>
        Object.entries(messages).reduce(
          (acc, [messageType, count]) => ({
            ...acc,
            [messageType]: (acc[messageType] || 0) + count,
          }),
          accumulated,
        ),
      {} as any,
    );
  }

  get sentKBytes() {
    return Object.values(this._data.sentKBytesPerPeerId).reduce(
      (acc, bytes) => acc + bytes,
      0,
    );
  }
}
